import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import groupService from '../services/groupService';
import '../styles/group-modals.css';

const JoinGroupModal = ({ isOpen, onClose, onGroupJoined, prefillCode = null }) => {
  const [groupCode, setGroupCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: enter code, 2: success
  const [joinedGroup, setJoinedGroup] = useState(null);
  
  const location = useLocation();

  // Handle prefilled code or URL params
  useEffect(() => {
    if (prefillCode) {
      setGroupCode(prefillCode);
    } else {
      // Check URL for group code (from shareable links)
      const urlParams = new URLSearchParams(location.search);
      const codeFromUrl = urlParams.get('code');
      if (codeFromUrl) {
        setGroupCode(codeFromUrl);
      }
    }
  }, [prefillCode, location.search]);

  // Handle input change
  const handleCodeChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Only digits
    if (value.length <= 8) {
      setGroupCode(value);
      setError(null);
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const numbers = pastedText.replace(/\D/g, '');
    if (numbers.length <= 8) {
      setGroupCode(numbers);
      setError(null);
    }
  };

  // Validate code
  const validateCode = () => {
    if (!groupCode.trim()) {
      setError('Please enter a group code');
      return false;
    }
    if (groupCode.length < 4 || groupCode.length > 8) {
      setError('Group code must be 4-8 digits');
      return false;
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateCode()) return;

    setLoading(true);
    setError(null);

    try {
      console.log('🚪 Joining group with code:', groupCode);
      const response = await groupService.joinGroup(groupCode);
      
      if (response.success) {
        console.log('✅ Joined group successfully:', response);
        setJoinedGroup(response.data);
        setStep(2);
        
        // Call success callback after showing success screen
        setTimeout(() => {
          onGroupJoined(response.data);
        }, 2000);
      } else {
        throw new Error(response.error || 'Failed to join group');
      }
    } catch (error) {
      console.error('❌ Error joining group:', error);
      
      // Handle specific error cases
      if (error.message.includes('not found')) {
        setError('Group not found. Please check the code and try again.');
      } else if (error.message.includes('already in group')) {
        setError('You are already a member of this group.');
      } else if (error.message.includes('full')) {
        setError('This group is full and cannot accept new members.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (loading) return; // Prevent closing during join
    
    setGroupCode('');
    setError(null);
    setStep(1);
    setJoinedGroup(null);
    onClose();
  };

  // Auto-submit when code is complete (disable auto-submit for variable length codes)
  // useEffect(() => {
  //   if (groupCode.length === 4 && !loading && !error) {
  //     // Auto-submit after a short delay
  //     const timer = setTimeout(() => {
  //       handleSubmit({ preventDefault: () => {} });
  //     }, 500);
  //     
  //     return () => clearTimeout(timer);
  //   }
  // }, [groupCode, loading, error]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {step === 1 ? '🚪 Join Group' : '🎉 Welcome to the Group!'}
          </h2>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {step === 1 ? (
          // Step 1: Enter group code
          <form onSubmit={handleSubmit} className="join-group-form">
            <div className="form-intro">
              <p>Enter the group code (4-8 digits) to join an investment group:</p>
            </div>

            <div className="form-group">
              <label htmlFor="groupCode">Group Code</label>
              <div className="clean-input-container">
                <input
                  type="text"
                  id="groupCode"
                  value={groupCode}
                  onChange={handleCodeChange}
                  onPaste={handlePaste}
                  placeholder="Enter 4-8 digit group code (e.g., 12345678)"
                  maxLength={8}
                  className="clean-code-input"
                  disabled={loading}
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <div className="input-status">
                  {groupCode && (
                    <span className={`length-indicator ${groupCode.length >= 4 && groupCode.length <= 8 ? 'valid' : 'invalid'}`}>
                      {groupCode.length} digit{groupCode.length !== 1 ? 's' : ''}
                      {groupCode.length >= 4 && groupCode.length <= 8 ? ' ✓' : ''}
                    </span>
                  )}
                </div>
              </div>
              <small className="help-text">
                💡 You can type or paste your group code here
              </small>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}


            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || groupCode.length < 4 || groupCode.length > 8}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner small" />
                    Joining...
                  </>
                ) : (
                  'Join Group'
                )}
              </button>
            </div>

            <div className="alternative-actions">
              <p>
                <small>
                  Don't have a code? 
                  <button 
                    type="button" 
                    className="link-button"
                    onClick={() => {
                      handleClose();
                      // This could trigger opening create group modal
                    }}
                  >
                    Create your own group
                  </button>
                </small>
              </p>
            </div>
          </form>
        ) : (
          // Step 2: Success screen
          <div className="success-content">
            <div className="success-icon">🎉</div>
            <h3>Successfully Joined!</h3>
            <p>
              Welcome to <strong>{joinedGroup?.name}</strong>! 
              You can now compete with other members and track your rankings.
            </p>
            
            {joinedGroup && (
              <div className="group-info-card">
                <h4>{joinedGroup.name}</h4>
                <p>
                  {joinedGroup.members?.length || 0} member{(joinedGroup.members?.length || 0) !== 1 ? 's' : ''}
                </p>
                {joinedGroup.description && (
                  <p className="group-description">{joinedGroup.description}</p>
                )}
              </div>
            )}

            <div className="success-actions">
              <button 
                onClick={handleClose}
                className="btn btn-primary"
              >
                View Group Rankings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinGroupModal;